import axios from 'axios'
import { pluckData } from 'src/utilities/http-client'
import type {
  AccreditationV2,
  EventAccreditation,
  EventInviteStatus,
  Instructor,
  LinkedService,
  QuestionSessionData,
  Syllabus,
} from 'src/v3/core/types'
import type { SurveyFormValues } from 'src/v3/components/survey'
import type { HydratedSessionV3 } from '../events-v2.slice'
import type {
  EventAttendanceMechanism,
  EventAttendanceMechanismWire,
  EventAttendancePendingWire,
  EventAttendanceProgress,
  EventAttendanceProgressWire,
  EventCertificate,
  EventCertificateProgressWire,
  EventCompletion,
  EventCompletionProgressWire,
  EventProgress,
  EventProgressWire,
  EventQuizProgress,
  EventQuizProgressWire,
  EventSurveyProgress,
  EventSurveyProgressWire,
  EventV2,
} from '../types'

let unhandledWireCase = (wire: never): never => {
  // eslint-disable-next-line fp/no-throw
  throw new Error(`Unhandled wire case: ${JSON.stringify(wire)}`)
}

let materializeAttendanceMechanism = (
  wire: EventAttendanceMechanismWire,
): EventAttendanceMechanism => {
  if (wire === 'managerTracked') return { kind: 'managerTracked' }
  if (wire === 'quizPasscode' || wire.quizPasscode !== undefined) {
    return { kind: 'quizPasscode' }
  }
  return unhandledWireCase(wire as never)
}

let transformEvents = (data: any[]): EventV2[] =>
  data.map((event): EventV2 => {
    let [eventType, location] = Object.entries<any>(event.eventType)[0]
    let flattenedLocation =
      eventType === 'hybrid' && Array.isArray(location) && location.length === 2
        ? { ...location[1], ...location[0], virtualName: location[1].name }
        : location
    return {
      ...event,
      attendanceTrackingMechanism: materializeAttendanceMechanism(
        event.attendanceTrackingMechanism,
      ),
      category: {
        kind: event.category.other
          ? 'other'
          : event.category === 'critical Care'
            ? 'criticalCare'
            : event.category,
        other: event.category.other,
      },
      categoryName:
        event.category.other != null
          ? 'Other'
          : ({
              'airway|Respiratory|Ventilation':
                'Airway|Respiratory|Ventilation',
              medical: 'Medical',
              trauma: 'Trauma',
              operations: 'Operations',
              'critical Care': 'Critical Care',
              cardiovascular: 'Cardiovascular',
              pediatrics: 'Pediatrics',
            }[event.category as string] ?? ''),
      location: {
        kind: eventType,
        ...flattenedLocation,
      },
      eventType:
        ({
          virtual: 'Virtual',
          inPerson: 'In Person',
          hybrid: 'Hybrid',
        }[eventType] as string) || 'Unknown',
    }
  })

export let getEventsV2 = (): Promise<EventV2[]> =>
  axios.get<any[]>('/api/v2/events').then(pluckData).then(transformEvents)

export let getLinkedServices = (): Promise<LinkedService[]> =>
  axios.get<LinkedService[]>('/api/event/linked-services').then(pluckData)

export let getInstructors = (): Promise<Instructor[]> =>
  axios.get<Instructor[]>('/api/event/instructors').then(pluckData)

export let getAccreditations = (): Promise<AccreditationV2[]> =>
  axios
    .get<{ [kind: string]: AccreditationV2 }[]>('/api/event/accreditations')
    .then(pluckData)
    .then((data) =>
      data.map((a): AccreditationV2 => {
        let [kind, obj] = Object.entries(a)[0]
        return { ...obj, kind: kind as any }
      }),
    )

export let getEventAccreditations = (): Promise<EventAccreditation[]> =>
  axios
    .get<EventAccreditation[]>('/api/event/accreditations/joins')
    .then(pluckData)

export let getSessionsV3 = (): Promise<HydratedSessionV3[]> =>
  axios.get<HydratedSessionV3[]>('/api/v3/event/sessions').then(pluckData)

export let postCompletionEvent = (
  sessionId: string,
  passcode?: string,
): Promise<void> =>
  axios
    .post<void>(
      `/api/v2/event/session/${sessionId}/completion-event`,
      passcode != null ? { passcode } : undefined,
    )
    .then(pluckData)

// The current attempt's quiz questions. Scoped by the restart boundary, unlike the V1
// /event/quiz/session/questions read, so a restarted attempt never sees the previous one's answers.
export let getEventQuizQuestions = (
  eventId: string,
): Promise<QuestionSessionData[]> =>
  axios
    .get<QuestionSessionData[]>(
      `/api/event/v2/${eventId}/quiz/session/questions`,
    )
    .then(pluckData)

export let postRestartEvent = (eventId: string): Promise<void> =>
  axios.post<void>(`/api/v2/event/${eventId}/restart-event`).then(pluckData)

export let downloadCertificateBlob = (url: string): Promise<Blob> =>
  axios.get<Blob>(url, { responseType: 'blob' }).then((r) => r.data)

export let postRsvpRegister = (
  sessionID: string,
): Promise<{ id: string; userID: string; status: EventInviteStatus }> =>
  axios
    .post<{ id: string; userID: string; status: EventInviteStatus }>(
      `/api/event/session/${sessionID}/accept`,
    )
    .then(pluckData)

export let putRsvpStatus = (
  inviteID: string,
  status: EventInviteStatus,
): Promise<void> =>
  axios
    .put<void>(`/api/event/invite/${inviteID}/status`, { status })
    .then(pluckData)

export let postEventSurveyV2 = (
  eventId: string,
  values: SurveyFormValues,
): Promise<void> =>
  axios.post<void>(`/api/event/v2/${eventId}/survey`, values).then(pluckData)

// ---- EventProgress materializers --------------------------------------

let materializeSelfCheckIn = (
  wire: EventAttendancePendingWire['selfCheckIn'],
): boolean => {
  if (wire === 'open') return true
  if (wire === 'closed') return false
  return unhandledWireCase(wire)
}

let materializeAttendanceProgress = (
  wire: EventAttendanceProgressWire,
): EventAttendanceProgress => {
  if (wire === 'notEligible') return { kind: 'notEligible' }
  if (wire === 'recorded') return { kind: 'recorded' }
  if (wire.pending !== undefined) {
    let { id, name, sessionTime, mechanism, selfCheckIn } = wire.pending
    return {
      kind: 'pending',
      sessionID: id,
      sessionName: name,
      sessionTime,
      mechanism: materializeAttendanceMechanism(mechanism),
      selfCheckInOpen: materializeSelfCheckIn(selfCheckIn),
    }
  }
  return unhandledWireCase(wire as never)
}

let materializeQuizProgress = (
  wire: EventQuizProgressWire,
): EventQuizProgress => {
  if (wire === 'notRequired') return { kind: 'notRequired' }
  if (wire === 'notAttempted') return { kind: 'notAttempted' }
  if (wire === 'passed') return { kind: 'passed' }
  if (wire.inProgress !== undefined) {
    return { kind: 'inProgress', sessionID: wire.inProgress }
  }
  if (wire.failed !== undefined) {
    let [score, passingScore] = wire.failed
    return { kind: 'failed', score, passingScore }
  }
  return unhandledWireCase(wire as never)
}

let materializeSurveyProgress = (
  wire: EventSurveyProgressWire,
): EventSurveyProgress => {
  if (wire === 'notRequired') return { kind: 'notRequired' }
  if (wire === 'notSubmitted') return { kind: 'notSubmitted' }
  if (wire.submitted !== undefined) {
    return { kind: 'submitted', surveyID: wire.submitted }
  }
  return unhandledWireCase(wire as never)
}

// Check notEarned and issued before the pending fall-through — the bare
// 'pending' string and { pending: ... } object both collapse to the same
// domain shape, so the payload-carrying cases must be narrowed first.
export let materializeEventCertificate = (
  wire: EventCertificateProgressWire,
): EventCertificate => {
  if (wire === 'pending') {
    return { kind: 'pending', requirementsCompletedTime: null }
  }
  if (wire === 'notApplicable') return { kind: 'notApplicable' }
  if (wire.notEarned !== undefined) {
    return { kind: 'notEarned', unmetRequirements: wire.notEarned }
  }
  if (wire.issued !== undefined) {
    let { certificateID, downloadURL, issuedTime, educationalEventDate } =
      wire.issued
    return {
      kind: 'issued',
      certificate: {
        certificateID,
        downloadURL,
        issuedTime: issuedTime ?? null,
        educationalEventDate: educationalEventDate ?? null,
      },
    }
  }
  // { pending: string | null } — requirementsCompletedTime may be null before
  // the backend records a timestamp. Intentional fallthrough without
  // unhandledWireCase: 'pending' is both a bare string (caught above) and a
  // single-key object, so the key check cannot distinguish a new unknown case.
  return { kind: 'pending', requirementsCompletedTime: wire.pending ?? null }
}

let materializeEventCompletion = (
  wire: EventCompletionProgressWire,
): EventCompletion => {
  if (wire === 'notCompleted') return { kind: 'notCompleted' }
  if (wire.completed !== undefined) {
    return { kind: 'completed', completedTime: wire.completed }
  }
  return unhandledWireCase(wire as never)
}

let materializeEventProgress = (wire: EventProgressWire): EventProgress => ({
  attendance: materializeAttendanceProgress(wire.attendance),
  quiz: materializeQuizProgress(wire.quiz),
  survey: materializeSurveyProgress(wire.survey),
  certificate: materializeEventCertificate(wire.certificate),
  completion: materializeEventCompletion(wire.completion),
})

export let getEventProgress = (eventId: string): Promise<EventProgress> =>
  axios
    .get<EventProgressWire>(`/api/event/v2/${eventId}/progress`)
    .then(pluckData)
    .then(materializeEventProgress)

export let getEventCertificate = (eventId: string): Promise<EventCertificate> =>
  axios
    .get<EventCertificateProgressWire>(`/api/event/v2/${eventId}/certificate`)
    .then(pluckData)
    .then(materializeEventCertificate)

export let getEventSyllabus = (eventId: string): Promise<Syllabus | null> =>
  axios
    .get<Syllabus>(`/api/event/${eventId}/syllabus`)
    .then(pluckData)
    .catch((err) => {
      if (err?.response?.status === 404) return null
      return Promise.reject(err)
    })
